/**
 * Form / Phone-call check detail card for the qualified-lead detail page.
 *
 * Surfaces the `ghl_form_call_lead_checks` row — the 1-hour job that, for
 * `google_ads`-mode locations, reads the GHL-applied `new survey lead` /
 * `new phone lead` tags and fires the matching Data Manager upload. Shows
 * whether it ran, which tags matched, which events uploaded, and — crucially —
 * WHY nothing uploaded when a tag matched (the skip reason carried in
 * `error_message`), so operators don't have to dig through Railway logs.
 *
 * Hidden entirely when the location isn't in google_ads mode (`enabled`),
 * matching the Google Ads upload cards beside it.
 */

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatRelativeTime } from '@/lib/ghl-qualifier'
import { useFormCallLeadCheck } from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly contactId: string
  /** When false, the card hides itself (location isn't in google_ads mode). */
  readonly enabled?: boolean
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  completed: { bg: 'bg-green-100', text: 'text-green-800' },
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-800' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  processing: { bg: 'bg-slate-100', text: 'text-slate-700' },
  failed: { bg: 'bg-red-100', text: 'text-red-800' },
  cancelled: { bg: 'bg-slate-100', text: 'text-slate-600' },
}

/** Human-friendly disposition labels for last_outcome. */
const OUTCOME_LABELS: Record<string, string> = {
  uploaded: 'Uploaded to Google Ads',
  matched_no_upload: 'Tag matched — no upload',
  no_match: 'No matching tags',
  contact_not_found: 'Contact not found',
}

export function FormCallCheckDetail({ contactId, enabled = true }: Props) {
  const query = useFormCallLeadCheck(contactId, enabled)

  if (!enabled) return null

  const row = query.data

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-semibold">Form / phone-call check (1h)</h3>
          {row && (
            <Badge
              className={`${(STATUS_COLORS[row.status] ?? STATUS_COLORS.pending).bg} ${
                (STATUS_COLORS[row.status] ?? STATUS_COLORS.pending).text
              } hover:bg-transparent`}
            >
              {row.status}
            </Badge>
          )}
        </div>

        {query.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !row ? (
          <p className="text-sm text-muted-foreground">
            No form/phone-call check on file for this contact yet.
          </p>
        ) : (
          <Body row={row} />
        )}
      </CardContent>
    </Card>
  )
}

function Body({ row }: { readonly row: NonNullable<ReturnType<typeof useFormCallLeadCheck>['data']> }) {
  const tags = row.tags_found ?? []
  const events = row.events_uploaded ?? []
  const outcomeLabel = row.last_outcome
    ? OUTCOME_LABELS[row.last_outcome] ?? row.last_outcome
    : '—'

  return (
    <div className="space-y-4">
      {/* Timing row */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>scheduled {formatRelativeTime(row.fire_at)}</span>
        {row.last_evaluated_at && <span>· ran {formatRelativeTime(row.last_evaluated_at)}</span>}
        {row.attempt_count != null && row.attempt_count > 0 && (
          <span>· {row.attempt_count} attempt{row.attempt_count === 1 ? '' : 's'}</span>
        )}
      </div>

      {/* Summary grid */}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground shrink-0">Outcome:</dt>
          <dd className="text-foreground">{outcomeLabel}</dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground shrink-0">Tags found:</dt>
          <dd className="flex flex-wrap gap-1">
            {tags.length > 0 ? (
              tags.map((t) => (
                <Badge key={t} variant="secondary" className="text-[11px]">
                  {t}
                </Badge>
              ))
            ) : (
              <span className="text-foreground">none</span>
            )}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground shrink-0">Events uploaded:</dt>
          <dd className="flex flex-wrap gap-1">
            {events.length > 0 ? (
              events.map((e) => (
                <Badge key={e} variant="secondary" className="text-[11px]">
                  {e}
                </Badge>
              ))
            ) : (
              <span className="text-foreground">none</span>
            )}
          </dd>
        </div>
      </dl>

      {/* Skip reason — why a matched tag didn't upload */}
      {row.last_outcome === 'matched_no_upload' && row.error_message && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-800 mb-1">No upload — reason</p>
          <pre className="text-xs text-amber-900 whitespace-pre-wrap break-words font-mono">
            {row.error_message}
          </pre>
        </div>
      )}

      {/* Hard failure (transient-exhausted / permanent) */}
      {row.status === 'failed' && row.error_message && row.last_outcome !== 'matched_no_upload' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-xs font-medium text-red-800 mb-1">Error</p>
          <pre className="text-xs text-red-900 whitespace-pre-wrap break-words font-mono">
            {row.error_message}
          </pre>
        </div>
      )}
    </div>
  )
}
